export const decodeJWT = (token: string): Record<string, unknown> | null => {
  try {
    const parts = token.split(".");

    if (parts.length !== 3) {
      return null;
    }

    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const binary = atob(base64);
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    const json = new TextDecoder().decode(bytes);

    return JSON.parse(json);
  } catch {
    return null;
  }
};

const PERMISSION_PATTERN = /^(GET|GET-ALL|POST|PUT|PATCH|DELETE)-/;

/** Extracts permission roles (GET-*, POST-*, etc.) from Keycloak token claims */
export const extractRolesFromToken = (tokenClaims: Record<string, unknown> | null): string[] => {
  if (!tokenClaims) {
    return [];
  }

  const allRoles: string[] = [];

  const realmAccess = tokenClaims.realm_access as { roles?: string[] } | undefined;
  if (realmAccess?.roles) {
    allRoles.push(...realmAccess.roles);
  }

  const resourceAccess = tokenClaims.resource_access as
    | Record<string, { roles?: string[] }>
    | undefined;
  if (resourceAccess) {
    Object.values(resourceAccess).forEach((clientAccess) => {
      if (clientAccess?.roles) {
        allRoles.push(...clientAccess.roles);
      }
    });
  }

  const directRoles = tokenClaims.roles as string[] | undefined;
  if (directRoles) {
    allRoles.push(...directRoles);
  }

  return [...new Set(allRoles.filter((role) => PERMISSION_PATTERN.test(role)))];
};

/** Extracts partnerId from token claims (identifies partner users) */
export const extractPartnerId = (
  tokenClaims: Record<string, unknown> | null,
): string | undefined => {
  if (!tokenClaims) {
    return undefined;
  }

  if (typeof tokenClaims.partnerId === "string") {
    return tokenClaims.partnerId;
  }

  const attributes = tokenClaims.attributes as { partnerId?: string | string[] } | undefined;
  if (attributes?.partnerId) {
    return Array.isArray(attributes.partnerId) ? attributes.partnerId[0] : attributes.partnerId;
  }

  return undefined;
};
